import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiMenu, FiLogOut, FiUser, FiArrowLeft, FiBookOpen, FiFileText, FiCheckCircle } from 'react-icons/fi';
import Sidebar from '../components/Sidebar';
import MarkdownContent from '../components/MarkdownContent';
import ChallengeView from '../components/ChallengeView';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Chapter = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chapter, setChapter] = useState(null);
  const [categoryId, setCategoryId] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [currentChallengeIndex, setCurrentChallengeIndex] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submittingChallengeId, setSubmittingChallengeId] = useState(null);
  const [challengeAttempts, setChallengeAttempts] = useState({});
  const [challengeFeedback, setChallengeFeedback] = useState({});

  useEffect(() => {
    fetchChapter();
    fetchChallenges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Atualizar feedback quando mudar de desafio e já houver tentativa aprovada
  useEffect(() => {
    const currentChallenge = challenges[currentChallengeIndex];
    
    if (currentChallenge && challengeAttempts[currentChallenge.id]) {
      const attempt = challengeAttempts[currentChallenge.id];
      
      // Se for múltipla escolha e já foi respondida corretamente
      if (currentChallenge.type === 'multiple_choice' && attempt.is_correct === 1) {
        setFeedback({
          type: 'success',
          message: 'Você já respondeu corretamente esta questão!',
          chapterCompleted: chapter?.completed === 1
        });
      }
      // Se for discursiva e já foi aprovada
      else if (currentChallenge.type === 'essay' && attempt.is_graded === 1 && attempt.grade >= 60) {
        setFeedback({
          type: 'info',
          message: 'Resposta aprovada! Você já completou esta questão.',
          needsGrading: false,
          chapterCompleted: chapter?.completed === 1
        });
      }
      // Se for discursiva e foi reprovada
      else if (currentChallenge.type === 'essay' && attempt.is_graded === 1 && attempt.grade < 60) {
        setFeedback({
          type: 'info',
          message: 'Resposta reprovada. Você pode reenviar uma nova resposta.',
          needsGrading: false
        });
      }
      // Se não foi respondida ainda, limpar feedback
      else if (!attempt.is_correct && !attempt.is_graded) {
        setFeedback(null);
      }
    } else {
      // Se não houver tentativa, limpar feedback (não mostrar "Carregando" se capítulo completo)
      if (!chapter?.completed) {
        setFeedback(null);
      }
    }
  }, [challenges, currentChallengeIndex, challengeAttempts, chapter]);

  const fetchChapter = async () => {
    try {
      const response = await axios.get(`${API_URL}/chapters/${id}`);
      const chapterData = response.data.chapter;
      setChapter(chapterData);
      setCategoryId(chapterData.category_id);
      if (!chapterData.isUnlocked) {
        alert('Você precisa completar todas as questões do tema anterior para acessar este conteúdo!');
        navigate('/dashboard');
        return;
      }
    } catch (error) {
      console.error('Erro ao buscar capítulo:', error);
      if (error.response?.status === 403) {
        alert('Você não tem permissão para acessar este conteúdo!');
      }
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchChallenges = async () => {
    try {
      const response = await axios.get(`${API_URL}/challenges/chapter/${id}`);
      const challenges = response.data.challenges;
      setChallenges(challenges);
      
      // Buscar tentativas anteriores para cada desafio
      const attemptsPromises = challenges.map(async (challenge) => {
        try {
          const attemptResponse = await axios.get(`${API_URL}/challenges/${challenge.id}/attempt`);
          return { challengeId: challenge.id, attempt: attemptResponse.data.attempt };
        } catch (error) {
          return { challengeId: challenge.id, attempt: null };
        }
      });
      
      const attemptsResults = await Promise.all(attemptsPromises);
      const attemptsMap = {};
      attemptsResults.forEach(({ challengeId, attempt }) => {
        attemptsMap[challengeId] = attempt;
      });
      setChallengeAttempts(attemptsMap);
    } catch (error) {
      console.error('Erro ao buscar desafios:', error);
    }
  };

  // Função para converter URL do YouTube para formato embed
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    
    // Se já está no formato embed, retorna direto
    if (url.includes('youtube.com/embed/')) {
      return url;
    }
    
    // Extrair ID do vídeo de diferentes formatos de URL
    let videoId = '';
    
    // Formato: https://www.youtube.com/watch?v=VIDEO_ID
    const watchMatch = url.match(/[?&]v=([^&]+)/);
    if (watchMatch) {
      videoId = watchMatch[1];
    }
    
    // Formato: https://youtu.be/VIDEO_ID
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) {
      videoId = shortMatch[1];
    }
    
    // Formato: https://www.youtube.com/watch?v=VIDEO_ID&t=123s
    if (!videoId && watchMatch) {
      videoId = watchMatch[1].split('&')[0];
    }
    
    if (videoId) {
      // Remover parâmetros extras do videoId
      videoId = videoId.split('&')[0].split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    
    // Se não conseguir extrair, retorna vazio
    console.warn('Não foi possível extrair o ID do vídeo do YouTube:', url);
    return '';
  };

  const renderVideoSection = (title, url) => {
    if (!url) {
      return null;
    }

    const embedUrl = getYouTubeEmbedUrl(url);

    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">{title}</h3>
        <div className="relative w-full bg-gray-900 rounded-lg overflow-hidden" style={{ paddingBottom: '56.25%' }}>
          {embedUrl ? (
            <iframe
              className="absolute top-0 left-0 w-full h-full"
              src={embedUrl}
              title={title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          ) : (
            <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center text-white">
              <div className="text-center">
                <p className="text-lg mb-2">Erro ao carregar vídeo</p>
                <p className="text-sm text-gray-400">URL do vídeo inválida: {url}</p>
                <p className="text-xs text-gray-500 mt-2">Use um link do YouTube válido</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleSubmitAnswer = async (answer, challengeId = null) => {
    setSubmitting(true);
    setSubmittingChallengeId(challengeId);
    setFeedback(null);
    
    // Limpar feedback específico desta questão
    if (challengeId) {
      setChallengeFeedback(prev => ({ ...prev, [challengeId]: null }));
    }

    try {
      // Se challengeId foi passado, usar ele. Senão, usar currentChallengeIndex
      const challenge = challengeId 
        ? challenges.find(c => c.id === challengeId)
        : challenges[currentChallengeIndex];
      
      if (!challenge) {
        throw new Error('Desafio não encontrado');
      }

      const payload = challenge.type === 'essay' 
        ? { essayAnswer: answer }
        : { selectedAnswer: answer };

      const response = await axios.post(
        `${API_URL}/challenges/${challenge.id}/submit`,
        payload
      );

      const feedbackData = {
        type: 'info',
        message: response.data.message,
        needsGrading: true,
        isResubmission: response.data.isResubmission
      };

      if (response.data.needsGrading) {
        // Se foi reenvio após reprovação, recarregar tentativas
        if (response.data.isResubmission) {
          setTimeout(() => {
            fetchChallenges(); // Recarregar para atualizar tentativas
            fetchChapter(); // Recarregar dados do capítulo
          }, 1000);
        }
        
        // Atualizar feedback específico desta questão
        if (challengeId) {
          setChallengeFeedback(prev => ({ ...prev, [challengeId]: feedbackData }));
        } else {
          setFeedback(feedbackData);
        }
        return;
      }

      if (response.data.alreadyApproved) {
        setFeedback({
          type: 'info',
          message: response.data.message,
          needsGrading: false
        });
        return;
      }

      if (response.data.isCorrect) {
        setFeedback({
          type: 'success',
          message: response.data.message,
          chapterCompleted: response.data.chapterCompleted
        });

        // Move to next challenge after 2 seconds
        if (!response.data.chapterCompleted && currentChallengeIndex < challenges.length - 1) {
          setTimeout(() => {
            setCurrentChallengeIndex(currentChallengeIndex + 1);
            setFeedback(null);
            fetchChapter(); // Refresh chapter data
          }, 2000);
        } else if (response.data.chapterCompleted) {
          // Chapter completed - refresh everything
          setTimeout(() => {
            fetchChapter(); // Refresh chapter data
            fetchChallenges(); // Refresh challenges
            // Force sidebar to refresh by triggering a window event
            window.dispatchEvent(new Event('chapterCompleted'));
          }, 2000);
        }
      } else {
        setFeedback({
          type: 'error',
          message: response.data.message
        });
      }
    } catch (error) {
      const errorFeedback = {
        type: 'error',
        message: error.response?.data?.error || 'Erro ao enviar resposta'
      };
      
      if (challengeId) {
        setChallengeFeedback(prev => ({ ...prev, [challengeId]: errorFeedback }));
      } else {
        setFeedback(errorFeedback);
      }
    } finally {
      setSubmitting(false);
      setSubmittingChallengeId(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-600 text-xl">Carregando...</div>
      </div>
    );
  }

  if (!chapter) {
    return null;
  }

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* Sidebar */}
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        categoryId={categoryId}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden text-gray-600 hover:text-gray-900"
              >
                <FiMenu className="text-xl" />
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="hidden lg:flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <FiArrowLeft />
                Voltar
              </button>
              <div>
                <h1 className="text-xl font-black tracking-tighter">
                  <span className="text-gray-900">J</span>
                  <span className="text-rose-600">&</span>
                  <span className="text-gray-900">M</span>
                </h1>
                <p className="text-sm text-gray-600">{chapter.title}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FiUser className="text-gray-400" />
                <span>{user?.username}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
              >
                <FiLogOut />
                <span className="hidden sm:inline">Sair</span>
              </button>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto bg-white">
          <div className="max-w-5xl mx-auto px-6 py-8">
            {/* Chapter Title - PRIMEIRO */}
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">{chapter.title}</h2>
              <p className="text-lg text-gray-600">{chapter.description}</p>
            </div>

            {/* Video Player - SEGUNDO (ANTES DOS DESAFIOS) */}
            {renderVideoSection('Introdução', chapter.video_url)}

            {/* Chapter Content - Explanation TERCEIRO (ANTES DOS DESAFIOS) */}
            <div className="bg-gray-50 rounded-lg p-8 mb-8 border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <FiBookOpen className="text-blue-600 text-xl" />
                <h3 className="text-xl font-bold text-gray-800">Explicação Passo a Passo</h3>
              </div>
              <div className="prose max-w-none">
                <MarkdownContent content={chapter.content} />
              </div>
            </div>

            {renderVideoSection('Mão na Massa', chapter.video_middle_url)}

            {/* Challenges Section - ÚLTIMO (DEPOIS DO VÍDEO E EXPLICAÇÃO) */}
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-2xl font-bold text-gray-800">Desafios e Exercícios</h3>
                  {chapter.completed === 1 && (
                    <span className="px-4 py-2 bg-green-100 text-green-800 rounded-lg font-semibold flex items-center gap-2">
                      <FiCheckCircle className="text-green-600" />
                      Capítulo Completo
                    </span>
                  )}
                </div>
                <p className="text-gray-600">
                  {chapter.completed === 1 
                    ? 'Você já completou todos os desafios deste capítulo. Veja suas respostas abaixo.'
                    : 'Complete os desafios para desbloquear o próximo capítulo'}
                </p>
              </div>

              {/* Sempre mostrar TODAS as questões para que o aluno veja o progresso */}
              {challenges.length > 0 ? (
                <div className="space-y-8">
                  {challenges.map((challenge, index) => {
                    const attempt = challengeAttempts[challenge.id];
                    
                    return (
                      <div key={challenge.id} className="border-b border-gray-200 pb-8 last:border-b-0">
                        <ChallengeView
                          challenge={challenge}
                          onSubmit={handleSubmitAnswer}
                          feedback={challengeFeedback[challenge.id] || (index === currentChallengeIndex ? feedback : null)}
                          submitting={submittingChallengeId === challenge.id || (index === currentChallengeIndex && submitting)}
                          challengeIndex={index}
                          totalChallenges={challenges.length}
                          attempt={attempt}
                          chapterCompleted={chapter?.completed === 1}
                          challengeId={challenge.id}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-600">
                  <FiFileText className="text-4xl mx-auto mb-4" />
                  <p>Nenhum desafio disponível para este capítulo.</p>
                </div>
              )}
            </div>

            {renderVideoSection('Conclusão e Curiosidades', chapter.video_conclusion_url)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chapter;
